import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { TruncatedText } from '@client/components/_ui/display/TruncatedText';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Space } from '@client/components/_ui/layout/Space';
import { RepairNetworkFormDrawer } from '@client/components/repair-networks/RepairNetworkFormDrawer';
import { RepairNetworkOwnerTypeTag } from '@client/components/repair-networks/RepairNetworkOwnerTypeTag';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, TableEmptyState, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { selectUser, useAuthStore } from '@client/stores/AuthStore';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import {
    REPAIR_NETWORK_OWNER_TYPE_LABELS,
    REPAIR_NETWORK_OWNER_TYPES,
} from '@shared/constants/business/repair-network';
import { hasPermission } from '@shared/permissions/roles';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Input, Select, Tag } from 'antd';
import { Info, Network } from 'lucide-react';
import { useState } from 'react';
import { SuppliersTabs } from '../~suppliers/SuppliersTabs';

export const Route = createFileRoute('/console/$orgId/configuration/vendor-management/repair-networks')({
    staticData: {
        // The list is a full page so a network record (also full page) replaces it
        // rather than stacking over it.
        presentation: 'fullPage',
        modules: ['claim'],
        permissions: ['supplier:read'],
        metadata: {
            title: 'Repair networks',
        },
        ui: {
            icon: Network,
            title: 'Repair networks',
            subtitle: 'Commercial groupings of repairers and where each one is offered',
            menuDescription: 'Group repairers into networks with coverage and ring-fencing',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <SuppliersTabs active="networks" />
            <NetworksList />
            <Outlet />
        </PageController>
    );
}

type QueryParams = RouterInputs['repairNetwork']['listRepairNetworks'];
type NetworkRow = RouterOutputs['repairNetwork']['listRepairNetworks']['rows'][number];

const columnHelper = createColumnHelper<NetworkRow, NonNullable<QueryParams['orderBy']>>();

const DETAILS_ROUTE = '/console/$orgId/configuration/vendor-management/repair-networks/details/$id';

function NetworksList() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { formatDate } = useFormatter();
    const user = useAuthStore(selectUser);
    const canEdit = hasPermission(user?.roles ?? [], 'supplier:update');
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [searchText, setSearchText] = useState('');

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        where: {},
    });
    const { data, error, isPending, refetch } = trpc.repairNetwork.listRepairNetworks.useQuery(input);
    // The bounded filter accepts a bare value or an `{ equals }` object; the select shows either.
    const ownerTypeFilter = input.where?.ownerType;
    const selectedOwnerType =
        typeof ownerTypeFilter === 'string'
            ? ownerTypeFilter
            : ownerTypeFilter && typeof ownerTypeFilter === 'object' && 'equals' in ownerTypeFilter
              ? ownerTypeFilter.equals
              : undefined;
    const hasActiveFilters = !!input.where?.name || !!input.where?.ownerType;

    const tableProps = usePrismaTable({
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        refreshable: { enabled: true, onRefresh: () => refetch() },
        emptyContent: (
            <TableEmptyState
                hasActiveFilters={hasActiveFilters}
                noRecordsTitle="No repair networks yet"
                noRecordsDescription="Create the first network and it becomes the organisation default every client draws from."
                noMatchesTitle="No networks match"
                noMatchesDescription="Try another name or owner type, or clear the filters."
            />
        ),
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Network',
                sortable: 'name',
                cell: ({ row }) => (
                    <div className="flex min-w-0 flex-col">
                        <span className="flex items-center gap-2">
                            <span className="font-semibold">{row.name}</span>
                            {row.isDefault ? (
                                <Tag variant="outlined" color="blue" className="m-0">
                                    Organisation default
                                </Tag>
                            ) : null}
                        </span>
                        {row.description ? (
                            <TruncatedText className="text-subtle-foreground text-xs">{row.description}</TruncatedText>
                        ) : null}
                    </div>
                ),
            }),
            columnHelper.accessor('ownerType', {
                id: 'ownerType',
                header: 'Owner',
                sortable: 'ownerType',
                cell: ({ row }) => (
                    <span className="flex items-center gap-2">
                        <RepairNetworkOwnerTypeTag value={row.ownerType} short />
                        {row.ownerName ? <span>{row.ownerName}</span> : null}
                    </span>
                ),
            }),
            columnHelper.accessor('memberCount', {
                id: 'memberCount',
                header: 'Members',
                align: 'right',
                width: 100,
                cell: ({ row }) => <span className="tabular-nums">{row.memberCount}</span>,
            }),
            columnHelper.accessor('ringFencedCount', {
                id: 'ringFencedCount',
                header: 'Ring-fenced',
                align: 'right',
                width: 110,
                cell: ({ row }) => (
                    <span className={row.ringFencedCount ? 'tabular-nums' : 'text-subtle-foreground tabular-nums'}>
                        {row.ringFencedCount}
                    </span>
                ),
            }),
            columnHelper.accessor('clientCount', {
                id: 'clientCount',
                header: 'Clients',
                align: 'right',
                width: 90,
                cell: ({ row }) => (
                    <span className={row.clientCount ? 'tabular-nums' : 'text-subtle-foreground tabular-nums'}>
                        {row.clientCount}
                    </span>
                ),
            }),
            columnHelper.accessor('updatedAt', {
                id: 'updatedAt',
                header: 'Updated',
                sortable: 'updatedAt',
                width: 130,
                cell: ({ row }) => <span className="text-subtle-foreground">{formatDate(row.updatedAt)}</span>,
            }),
            columnHelper.actions({
                width: 40,
                cell: ({ row }) => (
                    <div onClick={(event) => event.stopPropagation()}>
                        <Button.OpenInNewTab to={DETAILS_ROUTE} params={{ orgId, id: row.id }} />
                    </div>
                ),
            }),
        ],
    });

    return (
        <>
            <TableToolbar
                filters={
                    <Space>
                        <Input.Search
                            placeholder="Search networks"
                            allowClear
                            className="w-64"
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            onSearch={(value) =>
                                setInput((prev) => ({
                                    ...prev,
                                    offset: 0,
                                    where: {
                                        ...prev.where,
                                        name: value.trim()
                                            ? { contains: value.trim(), mode: 'insensitive' }
                                            : undefined,
                                    },
                                }))
                            }
                        />
                        <Select
                            placeholder="Owner type"
                            allowClear
                            className="w-52"
                            value={selectedOwnerType}
                            options={REPAIR_NETWORK_OWNER_TYPES.map((value) => ({
                                value,
                                label: REPAIR_NETWORK_OWNER_TYPE_LABELS[value],
                            }))}
                            onChange={(value: (typeof REPAIR_NETWORK_OWNER_TYPES)[number] | undefined) =>
                                setInput((prev) => ({
                                    ...prev,
                                    offset: 0,
                                    where: { ...prev.where, ownerType: value ? { equals: value } : undefined },
                                }))
                            }
                        />
                    </Space>
                }
                actions={
                    canEdit ? (
                        <Button.Add label="Add network" variant="primary" onClick={() => setIsAddOpen(true)} />
                    ) : null
                }
            />

            {error ? (
                <InlineQueryError error={error} onRetry={() => void refetch()} />
            ) : (
                <PrismaTable
                    {...tableProps}
                    onRow={({ id }) => ({
                        className: 'cursor-pointer',
                        onClick(event) {
                            navigate({ to: DETAILS_ROUTE, params: { orgId, id } }, event);
                        },
                    })}
                />
            )}

            <p className="mt-3 mb-0 flex items-start gap-2 text-subtle-foreground text-xs">
                <Info className="mt-0.5 size-3.5 shrink-0" />
                <span>
                    Every client draws its repairers from exactly one network. Clients without their own selection use
                    the organisation default.
                </span>
            </p>

            <RepairNetworkFormDrawer
                open={isAddOpen}
                onClose={() => setIsAddOpen(false)}
                onSaved={(network) => {
                    setIsAddOpen(false);
                    navigate({ to: DETAILS_ROUTE, params: { orgId, id: network.id } });
                }}
            />
        </>
    );
}
